// FIELD WORK — while the player clicks the clicker rapidly, a small CREW of citizens (up to FieldWorkConfig.crew, peasants first, then
// scribes if there are not enough peasants) go to the nearest rice paddy and work its rows at their normal walking pace: up and
// down every row, zig-zag, three times over, then on to the next-nearest paddy, and so on for as long as the clicking lasts.
// Everyone else keeps roaming; when the clicking stops the crew goes back to roaming. (The player's own cat is NOT involved: it
// stays where it is, so the camera that follows it stays smooth.)
//
// This file is pure logic (no scene objects, no timers) so it can be tested in Node: scene/peasants.js feeds it citizens, paddies
// and a walkable() test, and walks the citizens along what it returns.
// Tuning lives in balance.js (FieldWorkConfig). Tests: scripts/qa/fieldwork.test.mjs.

// ── "Rapid clicking" detector ──────────────────────────────────────────────────────────────────────────────────────────
// Rapid starts at `startClicks` clicker presses inside `startWindowMs`, and lasts until `stopIdleMs` pass with no press.
export function createRapidClicks(cfg) {
  let times = []
  let active = false
  let since = null // when the current streak of rapid clicking began (its first press)
  return {
    click(now) {
      times.push(now)
      while (times.length && now - times[0] > cfg.startWindowMs) times.shift()
      if (!active && times.length >= cfg.startClicks) { active = true; since = times[0] }
    },
    rapid(now) {
      if (active && (!times.length || now - times[times.length - 1] > cfg.stopIdleMs)) { active = false; times = []; since = null }
      return active
    },
    // How long the current streak has lasted, in ms (0 when not rapid) — and `since` is its start (null when not rapid).
    streakMs(now) { return this.rapid(now) ? now - since : 0 },
    streakStart(now) { return this.rapid(now) ? since : null },
    reset() { times = []; active = false; since = null },
  }
}

// The clicking-clan rule: rapid for longer than spec.rapidMs AND at least spec.minPresses presses in the last spec.rapidMs.
// (The pace condition is what stops a slow tap every couple of seconds from counting as "rapid clicking" for this.)
export function clickingClanDue(spec, streakMs, pressesInWindow) {
  return streakMs > spec.rapidMs && pressesInWindow >= spec.minPresses
}

// ── The route over one paddy ───────────────────────────────────────────────────────────────────────────────────────────
// field = { id, cx, cz, rows: [{ x, z0, z1 }] } (world units; rows sorted by x). Returns the points to run through, in order:
// a zig-zag over the rows (each run goes the length of a row, the hop to the next row happens at the row's end), done `passes`
// times, the rows reversed on every other pass so the cat never has to cross back over the whole field.
export function fieldRoute(field, from, passes) {
  const rows = [...field.rows].sort((a, b) => a.x - b.x)
  if (!rows.length) return []
  // Start at whichever of the four corner ends (first/last row × either end) is nearest the cat.
  let best = null
  for (const [rowIdx, r] of [[0, rows[0]], [rows.length - 1, rows[rows.length - 1]]]) {
    for (const z of [r.z0, r.z1]) {
      const d = Math.hypot(r.x - from.x, z - from.z)
      if (!best || d < best.d) best = { d, ascending: rowIdx === 0, atZ0: z === r.z0 }
    }
  }
  let order = best.ascending ? rows : [...rows].reverse()
  let atZ0 = best.atZ0
  const pts = []
  const add = (x, z) => {
    const last = pts[pts.length - 1]
    if (!last || Math.hypot(last.x - x, last.z - z) > 1e-6) pts.push({ x, z })
  }
  for (let p = 0; p < passes; p += 1) {
    for (const r of order) {
      add(r.x, atZ0 ? r.z0 : r.z1)
      atZ0 = !atZ0
      add(r.x, atZ0 ? r.z0 : r.z1)
    }
    order = [...order].reverse()
  }
  return pts
}

// ── Getting around a building: a small grid search ─────────────────────────────────────────────────────────────────────
// Cells are the same lattice the collision map uses (cell centres at multiples of `cell`), so walkable(cellCentre) is exact.
// Returns the points to walk through (the start is not included; the last one is exactly `to`), [] when a straight walk is
// clear, or null when there is no way through.
function lineClear(a, b, walkable, step) {
  const d = Math.hypot(b.x - a.x, b.z - a.z)
  const n = Math.max(1, Math.ceil(d / step))
  for (let i = 1; i <= n; i += 1) {
    const t = i / n
    if (!walkable(a.x + (b.x - a.x) * t, a.z + (b.z - a.z) * t)) return false
  }
  return true
}

export function findPath(from, to, walkable, { cell = 0.35, margin = 6, maxCells = 40000 } = {}) {
  if (lineClear(from, to, walkable, cell * 0.5)) return []
  const ci = (v) => Math.round(v / cell)
  const si = ci(from.x), sj = ci(from.z), gi = ci(to.x), gj = ci(to.z)
  if (!walkable(gi * cell, gj * cell) && !(gi === si && gj === sj)) return null
  for (const m of [margin, margin * 3, margin * 12]) {
    const i0 = Math.min(si, gi) - m, i1 = Math.max(si, gi) + m
    const j0 = Math.min(sj, gj) - m, j1 = Math.max(sj, gj) + m
    const W = i1 - i0 + 1, H = j1 - j0 + 1
    if (W * H > maxCells) continue
    const cells = searchGrid(si, sj, gi, gj, i0, j0, W, H, (i, j) => walkable(i * cell, j * cell))
    if (!cells) continue
    // cells → points, then keep only the corners (drop a point when its neighbours can see each other)
    const pts = cells.map(([i, j]) => ({ x: i * cell, z: j * cell }))
    pts[pts.length - 1] = { x: to.x, z: to.z }
    const out = []
    let anchor = from
    let k = 0
    while (k < pts.length) {
      let far = k
      for (let t = pts.length - 1; t > k; t -= 1) { if (lineClear(anchor, pts[t], walkable, cell * 0.4)) { far = t; break } }
      out.push(pts[far])
      anchor = pts[far]
      k = far + 1
    }
    return out
  }
  return null
}

// A* over a W×H box (8 directions, no corner cutting). Returns [[i, j], …] from the cell after the start to the goal, or null.
function searchGrid(si, sj, gi, gj, i0, j0, W, H, cellOk) {
  const idx = (i, j) => (j - j0) * W + (i - i0)
  const inBox = (i, j) => i >= i0 && i < i0 + W && j >= j0 && j < j0 + H
  const seen = new Int8Array(W * H) // 0 = unknown, 1 = walkable, 2 = blocked
  const ok = (i, j) => {
    if (!inBox(i, j)) return false
    const id = idx(i, j)
    if (seen[id] === 0) seen[id] = (i === si && j === sj) || cellOk(i, j) ? 1 : 2
    return seen[id] === 1
  }
  const g = new Float32Array(W * H).fill(Infinity)
  const from = new Int32Array(W * H).fill(-1)
  const closed = new Uint8Array(W * H)
  const heap = [] // [f, id]
  const push = (f, id) => { heap.push([f, id]); let c = heap.length - 1; while (c > 0) { const p = (c - 1) >> 1; if (heap[p][0] <= heap[c][0]) break; [heap[p], heap[c]] = [heap[c], heap[p]]; c = p } }
  const pop = () => {
    const top = heap[0], last = heap.pop()
    if (heap.length) {
      heap[0] = last
      let c = 0
      for (;;) {
        const l = c * 2 + 1, r = l + 1
        let m = c
        if (l < heap.length && heap[l][0] < heap[m][0]) m = l
        if (r < heap.length && heap[r][0] < heap[m][0]) m = r
        if (m === c) break
        ;[heap[m], heap[c]] = [heap[c], heap[m]]
        c = m
      }
    }
    return top
  }
  const h = (i, j) => Math.hypot(gi - i, gj - j)
  g[idx(si, sj)] = 0
  push(h(si, sj), idx(si, sj))
  while (heap.length) {
    const [, id] = pop()
    if (closed[id]) continue
    closed[id] = 1
    const i = (id % W) + i0, j = Math.floor(id / W) + j0
    if (i === gi && j === gj) {
      const out = []
      for (let cur = id; cur !== idx(si, sj); cur = from[cur]) out.push([(cur % W) + i0, Math.floor(cur / W) + j0])
      return out.reverse()
    }
    for (let dj = -1; dj <= 1; dj += 1) {
      for (let di = -1; di <= 1; di += 1) {
        if (!di && !dj) continue
        const ni = i + di, nj = j + dj
        if (!ok(ni, nj)) continue
        if (di && dj && (!ok(i + di, j) || !ok(i, j + dj))) continue // don't cut a corner
        const nid = idx(ni, nj)
        const cost = g[id] + (di && dj ? Math.SQRT2 : 1)
        if (cost < g[nid]) { g[nid] = cost; from[nid] = id; push(cost + h(ni, nj), nid) }
      }
    }
  }
  return null
}

// ── The crew ───────────────────────────────────────────────────────────────────────────────────────────────────────────
// Who goes: peasants first, then (only if there are not enough peasants) scribes. Within each kind, those nearest a paddy go.
// candidates: [{ id, peasant: bool, x, z }] (already filtered to those free to work). Returns the ids, at most n of them.
export function pickCrew(candidates, fields, n) {
  if (!fields.length || n <= 0) return []
  const d = (c) => Math.min(...fields.map((f) => Math.hypot(f.cx - c.x, f.cz - c.z)))
  const peasants = candidates.filter((c) => c.peasant).sort((a, b) => d(a) - d(b))
  const others = candidates.filter((c) => !c.peasant).sort((a, b) => d(a) - d(b))
  return [...peasants, ...others].slice(0, n).map((c) => c.id)
}

// A crew member's share of a paddy: with `crew` workers, worker `slot` (0-based) takes every crew-th row starting at its slot, so
// two workers never run the same row (rows are sorted by x). A paddy with fewer rows than workers leaves some workers a row-less
// share: they get the rows modulo as usual (possibly none), and the caller moves them on to the next paddy.
export function shareRows(field, slot, crew) {
  const rows = [...field.rows].sort((a, b) => a.x - b.x)
  return { ...field, rows: crew <= 1 ? rows : rows.filter((_, i) => i % crew === slot) }
}

// The next paddy for a worker standing at `pos`: the nearest one it has not worked this round (a paddy it has no share of is
// skipped). When it has worked them all, a new round starts (`worked` is cleared and the one it just did goes last); with only
// one paddy it simply does that one again. Returns null when there is nothing to work.
export function nextField(pos, fields, worked, slot, crew, lastId = null) {
  const usable = fields.filter((f) => shareRows(f, slot, crew).rows.length > 0)
  if (!usable.length) return null
  let cand = usable.filter((f) => !worked.has(f.id))
  let newRound = false
  if (!cand.length) {
    newRound = true
    cand = usable.filter((f) => f.id !== lastId)
    if (!cand.length) cand = usable
  }
  cand.sort((a, b) => Math.hypot(a.cx - pos.x, a.cz - pos.z) - Math.hypot(b.cx - pos.x, b.cz - pos.z))
  return { field: cand[0], newRound }
}

// The walk for one paddy: the way there (around buildings) followed by this worker's rows, `passes` times over.
// Returns { legs: [{x,z}…], travel: number of leading legs that are just the way there } or null when the paddy can't be reached.
export function planLegs(pos, field, slot, crew, passes, walkable) {
  const mine = shareRows(field, slot, crew)
  const route = fieldRoute(mine, pos, passes)
  if (!route.length) return null
  const way = findPath(pos, route[0], walkable)
  if (way === null) return null
  const travel = way.length ? way : [route[0]]
  return { legs: [...travel, ...route.slice(1)], travel: travel.length } // (the way there ends on route[0], so the rows continue from the 2nd point)
}
